import { useId } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import { gr, tokens } from "../tokens";
import icons from "../icons";
import GrIcon from "./GrIcon";
import GrIconButton from "./GrIconButton";

const tileSx = {
  position: "relative",
  flex: "none",
  width: gr.layout.fileTile,
  height: gr.layout.fileTile,
  borderRadius: gr.radius.tile,
  border: `1px solid ${gr.color.line}`,
  background: gr.color.bg,
  "& img": {
    width: "100%",
    height: "100%",
    display: "block",
    borderRadius: gr.radius.tile,
  },
};

const focusSx = {
  "&.Mui-focusVisible, &:focus-visible": {
    outline: "none",
    boxShadow: gr.shadow.focus,
  },
};

// Attached files (photos, PDFs, sheets…) as square tiles: photos show the picture (tap to see it
// large), other files show their file-type icon and open in a new tab. Optional × on each tile
// (onRemove) and an "add" tile with the file picker (onAdd = the input's onChange) for forms.
// files = [{ src, name?, image?, href? }]: src = preview or file-type icon, href = the full file.
export default function GrAttachments({
  files = [],
  onOpen,
  onRemove,
  onAdd,
  accept,
  multiple = true,
  hint,
  addLabel = "Add files",
  sx,
}) {
  const inputId = `gr-attach-${useId()}`;
  if (!files.length && !onAdd) return null;
  return (
    <Box sx={{ fontFamily: gr.font.family, ...sx }}>
      {hint ? (
        <Box
          component="p"
          sx={{
            m: 0,
            mb: gr.space[2],
            fontSize: gr.font.size.xs,
            color: gr.color.muted,
          }}
        >
          {hint}
        </Box>
      ) : null}
      <Box
        component="ul"
        sx={{
          listStyle: "none",
          m: 0,
          p: 0,
          display: "flex",
          flexWrap: "wrap",
          gap: gr.space[3],
        }}
      >
        {files.map((file, index) => {
          const name = file.name || (file.image ? "Photo" : "File");
          const preview = (
            <Box
              component="img"
              src={file.src}
              alt=""
              loading="lazy"
              width={tokens.layout.fileTile}
              height={tokens.layout.fileTile}
              sx={{ objectFit: file.image ? "cover" : "contain", p: file.image ? 0 : gr.space[3] }}
            />
          );
          return (
            <Box component="li" key={file.key ?? index} sx={tileSx}>
              {file.image && onOpen ? (
                <ButtonBase
                  aria-label={`Open ${name}`}
                  onClick={() => onOpen(index)}
                  sx={{ width: "100%", height: "100%", borderRadius: gr.radius.tile, ...focusSx }}
                >
                  {preview}
                </ButtonBase>
              ) : file.href ? (
                <Box
                  component="a"
                  href={file.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Open ${name}`}
                  sx={{ display: "block", width: "100%", height: "100%", borderRadius: gr.radius.tile, ...focusSx }}
                >
                  {preview}
                </Box>
              ) : (
                preview
              )}
              {onRemove ? (
                <GrIconButton
                  size="sm"
                  aria-label={`Remove ${name}`}
                  onClick={() => onRemove(index)}
                  sx={{
                    position: "absolute",
                    top: `calc(-1 * ${gr.space[2]})`,
                    right: `calc(-1 * ${gr.space[2]})`,
                    width: gr.control.sm,
                    height: gr.control.sm,
                    borderRadius: gr.radius.pill,
                    boxShadow: gr.shadow.raised,
                  }}
                >
                  <GrIcon icon={icons.close} />
                </GrIconButton>
              ) : null}
            </Box>
          );
        })}
        {onAdd ? (
          <Box component="li" sx={{ ...tileSx, background: gr.color.surface }}>
            <input
              id={inputId}
              type="file"
              accept={accept}
              multiple={multiple}
              onChange={(e) => {
                onAdd(e);
                // Clear it so picking the same file again (after removing it) still works.
                e.target.value = "";
              }}
              style={{ display: "none" }}
            />
            <Box
              component="label"
              htmlFor={inputId}
              tabIndex={0}
              role="button"
              aria-label={addLabel}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  document.getElementById(inputId)?.click();
                }
              }}
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: gr.radius.tile,
                border: `1px dashed ${gr.color.lineStrong}`,
                color: gr.color.brand,
                cursor: "pointer",
                transition: "background-color .15s ease, border-color .15s ease",
                "&:hover": { background: gr.color.tint, borderColor: gr.color.brand },
                "&:focus-visible": { outline: "none", boxShadow: gr.shadow.focus },
                "& svg": { width: gr.icon.xl, height: gr.icon.xl },
              }}
            >
              <GrIcon icon={icons.upload} />
            </Box>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}

GrAttachments.propTypes = {
  files: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      src: PropTypes.string.isRequired,
      name: PropTypes.string,
      image: PropTypes.bool,
      href: PropTypes.string,
    })
  ),
  // Photo tile clicked (show it large); other files open their href.
  onOpen: PropTypes.func,
  onRemove: PropTypes.func,
  // The file input's onChange; shows the add tile.
  onAdd: PropTypes.func,
  accept: PropTypes.string,
  multiple: PropTypes.bool,
  hint: PropTypes.node,
  addLabel: PropTypes.string,
  sx: PropTypes.object,
};
